const all = new Map<number, GeckoAnglePlotSetting>()

export const anglePlotLookupTable = new Map<number, string>()

export const anglePlotLookup = (id: number): string => {
  return anglePlotLookupTable.get(id) || `Plot #${id}`
}

/**
 * Angle plot settings are used to represent things like scans along the
 * "back side" of an asset. This class is used to represent a single angle
 * plot setting, a number currently populated in Portal.
 */
export class GeckoAnglePlotSetting {
  //----------------------------------------------------------------------------

  public static readonly Description = 'The angle plot setting to display'

  public static readonly None = new GeckoAnglePlotSetting(
    -1,
    'None',
    'No angle plot setting provided'
  )

  public static readonly WallThicknessPlot = new GeckoAnglePlotSetting(
    1,
    'Wall Thickness Plot',
    '',
    'all',
    ''
  )

  public static readonly Crown = new GeckoAnglePlotSetting(
    152,
    'Crown',
    '',
    'all',
    'angle = 0'
  )

  public static readonly Left = new GeckoAnglePlotSetting(
    153,
    'Left',
    '',
    'front',
    '(angle <= -15)'
  )

  public static readonly Right = new GeckoAnglePlotSetting(
    154,
    'Right',
    '',
    'front',
    '(angle >= 15)'
  )

  public static readonly NorthSide = new GeckoAnglePlotSetting(
    17,
    'North Side',
    '',
    'front',
    '(angle between -90 and 90)'
  )

  public static readonly SouthSide = new GeckoAnglePlotSetting(
    18,
    'South Side',
    '',
    'back',
    '(angle > 90) or (angle < -90)'
  )

  public static readonly EastSide = new GeckoAnglePlotSetting(
    54,
    'East Side',
    '',
    'front',
    '(angle between -90 and 90)'
  )

  public static readonly WestSide = new GeckoAnglePlotSetting(
    55,
    'West Side',
    '',
    'back',
    '(angle > 90) or (angle < -90)'
  )

  public static readonly EastSideInverseQuery = new GeckoAnglePlotSetting(
    150,
    'East Side',
    '',
    'back',
    '(angle > 90) or (angle < -90)'
  )

  public static readonly WestSideInverseQuery = new GeckoAnglePlotSetting(
    151,
    'West Side',
    '',
    'front',
    '(angle between -90 and 90)'
  )

  public static readonly AutomaticValidation = new GeckoAnglePlotSetting(
    32,
    'Automatic Validation',
    '',
    'all',
    'auto_validated = True'
  )

  public static readonly LadoLesteDireito = new GeckoAnglePlotSetting(
    37,
    'Lado Leste (Direito)',
    '',
    'front',
    '(angle between -90 and 90)'
  )

  public static readonly LadoOesteEsquerdo = new GeckoAnglePlotSetting(
    38,
    'Lado Oeste (Esquerdo)',
    '',
    'back',
    '(angle < -90) or (angle > 90)'
  )

  //----------------------------------------------------------------------------

  /** A unique ID which should line up with Data Systems and even Portal */
  public readonly id: number

  /** A user-facing name for this object, or its ID if none is populated */
  public readonly name: string

  /** A user-facing description for this a specific angle plot setting */
  public readonly description: string

  /** A data team utilized field for the camera orientation */
  public readonly camera_orientation: string | undefined

  /** A data team utilized field for the query */
  public readonly query: string | undefined

  private constructor(
    id: number,
    name: string,
    description: string,
    camera_orientation?: string,
    query?: string
  ) {
    this.id = id
    this.name = name
    this.description = description
    this.camera_orientation = camera_orientation
    this.query = query
    all.set(id, this)
    anglePlotLookupTable.set(id, this.name)
  }

  //----------------------------------------------------------------------------

  /**
   * A default sorting method for component data frame angle plot settings
   * @param a The first angle plot setting object
   * @param b The second angle plot setting object
   */
  public static DefaultSort(
    a: GeckoAnglePlotSetting,
    b: GeckoAnglePlotSetting
  ): number {
    return a.id - b.id
  }

  /**
   * Returns the unique angle plot setting instance for a given ID if it exists
   * @param id The unique ID of this angle plot setting
   */
  public static GetByID(id: number | undefined): GeckoAnglePlotSetting {
    return id === undefined
      ? GeckoAnglePlotSetting.None
      : (all.get(id) ?? GeckoAnglePlotSetting.None)
  }

  /**
   * Returns the unique angle plot setting instance for a given ID, or creates
   * a unique angle plot setting instance from scratch for this new ID
   * @param id The unique ID of this object
   */
  public static GetOrCreate(
    id: number | undefined,
    camera_orientation?: string,
    query?: string
  ): GeckoAnglePlotSetting {
    return id === undefined
      ? GeckoAnglePlotSetting.None
      : (all.get(id) ??
          new GeckoAnglePlotSetting(
            id,
            anglePlotLookup(id),
            'The plot this data was pulled from',
            camera_orientation,
            query ?? ''
          ))
  }
}
